import React from "react";
import Table from "./Table";
import NCS_REF from "../data/NCS_REF.json";

interface MotorTableProps {
  rows: Array<Record<string, string>>;
  setRows: React.Dispatch<React.SetStateAction<Array<Record<string, string>>>>;
  nerveOptions: string[];
  recOptions: string[];
  stimOptions: string[];
  sideOptions: string[];
}

const nerveGroups: Record<string, string[]> = {
  UEx: [
    "Median",
    "Ulnar",
    "Ulnar (inching)",
    "Radial",
    "Axillary",
    "Musculocutaneous",
    "Suprascapular",
    "Accessory",
  ],
  LEx: ["Peroneal", "Tibial", "Femoral"],
  Facial: ["Facial"],
};

const MotorTable: React.FC<MotorTableProps> = ({
  rows,
  setRows,
  nerveOptions,
  recOptions,
  stimOptions,
  sideOptions,
}) => {
  const headers = [
    "Nerve",
    "Side",
    "Stim",
    "Rec",
    "Lat(ms)",
    "Amp(mV)",
    "Dur(ms)",
    "Dist(cm)",
    "CV(m/s)",
    "Note",
  ];

  const addStimRows = (fullNerve: string) => {
    if (!fullNerve.includes(" - ")) return;
    const [nerve, rec] = fullNerve.split(" - ");
    const segments = (NCS_REF as any).motor_ncs?.[nerve]?.segments || [];

    const filteredSegments = segments.filter((segment: any) => segment.recording_site === rec);

    const newRows: Array<Record<string, string>> = [];

    sideOptions.forEach((side) => {
      filteredSegments.forEach((segment: any, stimIndex: number) => {
        const isUlnarInching = nerve === "Ulnar (inching)";
        const fixed2cmSites = ["D4", "D2", "Elbow", "P2", "P4", "P6"];
        const dist =
          isUlnarInching && fixed2cmSites.includes(segment.stimulation_site) ? "2.0" : "";

        newRows.push({
          Nerve: stimIndex === 0 ? nerve : "",
          Side: stimIndex === 0 ? side : "",
          Stim: segment.stimulation_site,
          Rec: rec,
          "Lat(ms)": "",
          "Amp(mV)": "",
          "Dur(ms)": "",
          "Dist(cm)": dist,
          "CV(m/s)": "",
          Note: "",
        });
      });
    });

    setRows((prev) => {
      const updated = [...prev];
      let filled = 0;

      for (let index = 0; index < updated.length && filled < newRows.length; index++) {
        const row = updated[index];
        const isEmpty = !row.Nerve && !row.Stim && !row.Rec && !row.Side;
        if (isEmpty) {
          updated[index] = { ...updated[index], ...newRows[filled] };
          filled++;
        }
      }

      if (filled < newRows.length) {
        return [...updated, ...newRows.slice(filled)];
      }

      return updated;
    });
  };

  return (
    <section className="section-block">
      <div className="section-header">
        <h2 className="section-title">Motor NCS</h2>

        <div className="toolbar">
          {["UEx", "LEx", "Facial"].map((group) => (
            <select
              key={group}
              className="app-select compact-select"
              onChange={(event) => {
                addStimRows(event.target.value);
                event.target.value = "";
              }}
            >
              <option value="">{`+ Add Nerve (${group})`}</option>
              {nerveGroups[group].flatMap((nerve) => {
                const segments = (NCS_REF as any).motor_ncs?.[nerve]?.segments || [];
                const uniqueSegments = Array.from(
                  new Map(segments.map((segment: any) => [segment.recording_site, segment])).values()
                );

                return uniqueSegments.map((segment: any) => {
                  const label = `${nerve} - ${segment.recording_site}`;
                  return (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  );
                });
              })}
            </select>
          ))}
        </div>
      </div>

      <Table
        title=""
        headers={headers}
        nerveOptions={nerveOptions}
        sideOptions={sideOptions}
        stimOptions={stimOptions}
        recOptions={recOptions}
        muscleOptions={[]}
        rows={rows}
        setRows={setRows}
        isMotor={true}
        dropdownOptions={{
          Note: [
            "Absent",
            "Prolonged",
            "Slow",
            "Decreased",
            "Prolonged, Decreased",
            "Slow, Decreased",
          ],
        }}
      />
    </section>
  );
};

export default MotorTable;
